import { useEffect, useState } from 'react';
import { Box, CircularProgress, LinearProgress, Stack, Typography, alpha } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

type ImportProcessingVariant = 'analyze' | 'preview';

interface ImportProcessingViewProps {
  variant?: ImportProcessingVariant;
  rowCount?: number;
}

interface ProcessingStage {
  label: string;
  startsAtMs: number;
}

interface VariantConfig {
  title: string;
  stages: ProcessingStage[];
  // Time constant for the progress easing — smaller = bar fills faster.
  progressTimeConstantMs: number;
}

const VARIANTS: Record<ImportProcessingVariant, VariantConfig> = {
  analyze: {
    title: 'Analizando el archivo',
    stages: [
      { label: 'Leyendo el archivo', startsAtMs: 0 },
      { label: 'Detectando columnas y encabezados', startsAtMs: 800 },
      { label: 'Sugiriendo el mapeo', startsAtMs: 2200 },
    ],
    progressTimeConstantMs: 3500,
  },
  preview: {
    title: 'Analizando tus movimientos',
    stages: [
      { label: 'Leyendo el archivo', startsAtMs: 0 },
      { label: 'Detectando posibles duplicados', startsAtMs: 1500 },
      { label: 'Clasificando categorías con IA', startsAtMs: 4000 },
      { label: 'Preparando la vista previa', startsAtMs: 16000 },
    ],
    progressTimeConstantMs: 8000,
  },
};

const TICK_MS = 200;

const getDescription = (variant: ImportProcessingVariant, rowCount?: number): string => {
  if (variant === 'analyze') {
    return 'Estamos leyendo el archivo y detectando las columnas. Solo tardará un momento.';
  }
  if (rowCount) {
    return `Estamos revisando ${rowCount} ${
      rowCount === 1 ? 'movimiento' : 'movimientos'
    } y clasificándolos automáticamente. Esto puede tardar unos segundos.`;
  }
  return 'Estamos revisando tus movimientos y clasificándolos automáticamente. Esto puede tardar unos segundos.';
};

export const ImportProcessingView = ({
  variant = 'preview',
  rowCount,
}: ImportProcessingViewProps) => {
  const { title, stages, progressTimeConstantMs } = VARIANTS[variant];
  const description = getDescription(variant, rowCount);
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, TICK_MS);
    return () => window.clearInterval(interval);
  }, []);

  const activeStageIndex = stages.reduce(
    (current, stage, index) => (elapsedMs >= stage.startsAtMs ? index : current),
    0
  );

  // Eases toward ~92% so the bar keeps moving but never falsely completes.
  const progress = 92 * (1 - Math.exp(-elapsedMs / progressTimeConstantMs));

  return (
    <Stack spacing={4} alignItems="center" sx={{ py: 6, minHeight: 360 }}>
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={88} thickness={2.5} />
        <Box
          sx={{
            position: 'absolute',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          }}
        >
          <AutoAwesomeIcon color="primary" />
        </Box>
      </Box>

      <Stack spacing={1} alignItems="center" textAlign="center">
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
          {description}
        </Typography>
      </Stack>

      <Box sx={{ width: '100%', maxWidth: 420 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>

      <Stack spacing={1.25} sx={{ width: '100%', maxWidth: 420 }}>
        {stages.map((stage, index) => {
          const isDone = index < activeStageIndex;
          const isActive = index === activeStageIndex;
          return (
            <Stack key={stage.label} direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {isDone ? (
                  <CheckRoundedIcon color="success" sx={{ fontSize: 20 }} />
                ) : isActive ? (
                  <CircularProgress size={16} thickness={5} />
                ) : (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'action.disabled',
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: isDone || isActive ? 'text.primary' : 'text.disabled',
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {stage.label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>

      <Typography variant="caption" color="text.secondary">
        No cierres esta ventana mientras procesamos el archivo.
      </Typography>
    </Stack>
  );
};
